import { useEffect, useRef } from "react";
import slide1 from "./../assets/slide_1.mp4";
import slide2 from "./../assets/slide_2.mp4";
import slide4 from "./../assets/slide_4.mp4";
import aiSizingImage from "./../assets/ai-sizing-image.png";
import aiFitPrediction from "./../assets/ai-fit-prediction-image.png";
import SEOLandingSection from "./SEOLandingSection";
import apparelSizingSoftwareImage from "./../assets/apparel-sizing-software-image.png";
import securityDataPrivacyImage from "./../assets/security-data-privacy-image.png";

const AISizingSoftwareSections = () => {
  const videoRefs = useRef([]);
  const hasPlayedRef = useRef([]);
  const endedHandlersRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const index = Number(video.dataset.index);

          if (entry.isIntersecting) {
            if (!hasPlayedRef.current[index] && video.paused && !video.ended) {
              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(() => {});
              }
            }
          } else {
            if (!video.paused && !video.ended) {
              video.pause();
            }
          }
        });
      },
      { threshold: 0.5 },
    );

    const videos = videoRefs.current.filter(Boolean);

    videos.forEach((video) => {
      const index = Number(video.dataset.index);
      hasPlayedRef.current[index] = false;

      const handleEnded = () => {
        hasPlayedRef.current[index] = true;
      };

      endedHandlersRef.current[index] = handleEnded;
      video.addEventListener("ended", handleEnded);
      observer.observe(video);
    });

    return () => {
      videos.forEach((video) => {
        observer.unobserve(video);
        const index = Number(video.dataset.index);
        const handler = endedHandlersRef.current[index];
        if (handler) {
          video.removeEventListener("ended", handler);
        }
      });
      observer.disconnect();
    };
  }, []);

  return (
    <div className="font-normal text-[14px] leading-[23.4px] tracking-[0.17px] text-[#4A5565]">
      <SEOLandingSection
        heroImage={aiSizingImage}
        bgColor="#F7F6F2"
        heading="What Is AI Sizing Software?"
      >
        <div>
          <div className="mb-[20px]">
            AI sizing software uses machine learning and body modeling to
            predict the best clothing size for online shoppers. Unlike static
            size charts, AI apparel sizing software analyzes body measurements,
            garment specifications, and shopper preferences to produce accurate
            size recommendations.
          </div>

          <div>
            <ul>
              <li>• Body measurement modeling</li>
              <li>• Garment fit prediction</li>
              <li>• Apparel fit analytics</li>
              <li>• Size recommendation engines</li>
              <li>• Ecommerce integration</li>
            </ul>
          </div>
        </div>
      </SEOLandingSection>

      <SEOLandingSection
        heroImage={aiFitPrediction}
        bgColor="#EBF0FF"
        heading="How AI Fit Prediction Software Works"
      >
        <div>
          <div className="mb-[20px]">
            <ul>
              <li>Step 1 - Data Input</li>
              <li>Step 2 - Body Modeling</li>
              <li>Step 3 - Garment Mapping</li>
              <li>Step 4 - Size Prediction</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>• Body measurement modeling</li>
              <li>• Garment fit prediction</li>
              <li>• Apparel fit analytics</li>
              <li>• Size recommendation engines</li>
              <li>• Ecommerce integration</li>
            </ul>
          </div>
        </div>
      </SEOLandingSection>

      <SEOLandingSection
        heroImage={apparelSizingSoftwareImage}
        bgColor="#E4E4E4"
        heading="Apparel Sizing Software vs Traditional Size Charts"
      >
        <ul className="">
          <li>Interactive user experience for SMS Marketing</li>
          <li>
            Anonymized segment led marketing (by sizing, body and face shape,
            color analysis, height, and more)
          </li>
          <li>Personalized with Agentic Capabilities</li>
        </ul>
      </SEOLandingSection>

      <SEOLandingSection
        heading="Size Recommendation Software for Ecommerce Brands"
        showVideo={true}
        videoSrc={slide2}
        videoRef={(el) => {
          videoRefs.current[0] = el;
        }}
        videoIndex={0}
        ctaLink="/get-started"
        ctaText="Get Started"
      >
        <ul className="space-y-2 text-[#47423D]">
          <li>
            Incorrect sizing is one of the primary causes of fashion ecommerce
            returns
          </li>
          <li>Shoppers are more likely to purchase when sizing is clear.</li>
          <li>Size recommendation software reduces purchase uncertainty.</li>
          <li>
            Apparel ecommerce return rates typically range between 20-40%.
          </li>
        </ul>
      </SEOLandingSection>

      <SEOLandingSection
        heading="Benefits of AI Sizing Software for Fashion Brands"
        showVideo={true}
        videoSrc={slide4}
        videoRef={(el) => {
          videoRefs.current[1] = el;
        }}
        videoIndex={1}
        ctaLink="/get-started"
        ctaText="Get Started"
      >
        <div className="space-y-4 text-[#47423D] mt-2">
          <ul className="space-y-2">
            <li>Reduce Returns</li>
            <li>Improve Conversion Rate</li>
            <li>Increase Customer Confidence</li>
            <li>Product-Level Fit Intelligence</li>
          </ul>
        </div>
      </SEOLandingSection>

      <SEOLandingSection
        heading="Key Features of AI Apparel Sizing Software"
        showVideo={true}
        videoSrc={slide1}
        videoRef={(el) => {
          videoRefs.current[2] = el;
        }}
        videoIndex={2}
        ctaLink="/get-started"
        ctaText="Get Started"
      >
        <div className="space-y-4">
          <ul className="text-[#47423D] mt-[10px] space-y-2">
            <li>SKU-Level Modeling</li>
            <li>API integrations</li>
            <li>Privacy compliance</li>
            <li>Real-time predictions</li>
            <li>Style-specific accuracy</li>
            <li>Real-time predictions</li>
            <li>Cross-device compatibility</li>
          </ul>
        </div>
      </SEOLandingSection>

      <div className="pb-4 md:pb-6 bg-[#DADADA] mb-[40px] md:mb-[60px]">
        <SEOLandingSection
          heroImage={securityDataPrivacyImage}
          bgColor="#EDFCFF"
          heading="Security, Data Privacy & Compliance"
          source="security"
        >
          <div className="space-y-4">
            <ul className="text-[#47423D] mt-[10px] space-y-2">
              <li>GDPR alignment</li>
              <li>SOC2 readiness</li>
              <li>Data anonymization</li>
              <li>Secure cloud storage</li>
            </ul>
          </div>
        </SEOLandingSection>
      </div>
    </div>
  );
};

export default AISizingSoftwareSections;
